import { faStar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Button from 'components/button'
import { FreestyleFavoriteWorkout } from 'lib/favoriteWorkouts'
import useFavoriteWorkouts from 'lib/hooks/useFavoriteWorkouts'
import { INRWorkoutSession, WorkoutName } from 'interfaces/workout'
import { useSnackbar } from 'store/config/selectors'
import { useWorkoutActions, useWorkoutSession } from 'store/workout/selectors'
import './favorite-button.scss'

interface Props {
  workoutSession?: INRWorkoutSession
  className?: string
}

const FavoriteButton = ({ workoutSession: suppliedWorkoutSession, className = '' }: Props) => {
  const currentWorkoutSession = useWorkoutSession()
  const workoutSession = suppliedWorkoutSession ?? currentWorkoutSession
  const { updateWorkoutSession } = useWorkoutActions()
  const { isFavorite, loading, toggleFavorite } = useFavoriteWorkouts()
  const {
    closeSnackbar,
    setFavoriteAddedSnackbar,
    setFavoriteRemovedSnackbar,
    setFavoritesLimitSnackbar,
  } = useSnackbar()
  const selected = isFavorite(workoutSession)

  if (workoutSession.name === WorkoutName.QUICKSTART) return null

  const toggle = async () => {
    closeSnackbar()
    const result = await toggleFavorite(workoutSession)

    if (result.limitReached) {
      setFavoritesLimitSnackbar()
      return
    }

    if (!result.favorite) return

    if (result.added) {
      if (result.favorite.kind === 'freestyle') {
        const favorite = result.favorite as FreestyleFavoriteWorkout
        // Keep performed hits and timestamps in the live session. The stored
        // Favorite is intentionally a clean, reusable preset.
        updateWorkoutSession({
          ...workoutSession,
          favoriteId: favorite.favoriteId,
          favoriteImage: favorite.favoriteImage,
          favoriteImageKey: favorite.imageKey,
        })
      }
      setFavoriteAddedSnackbar()
      return
    }

    setFavoriteRemovedSnackbar()
  }

  const label = selected ? 'Remove from Favorites' : 'Add to Favorites'

  return (
    <Button
      ariaLabel={label}
      ariaPressed={selected}
      className={`favoriteButton ${selected ? 'favoriteButton--selected' : ''} ${className}`}
      customClickEvent={toggle}
      disabled={loading}
      icon
      title={label}
    >
      <span className="favoriteButton__star">
        <FontAwesomeIcon className="favoriteButton__glyph" icon={faStar} />
      </span>
    </Button>
  )
}

export default FavoriteButton
